'use client'

import { AddIconNoCircle } from '@repo/ui/AddIconNoCircle'
import { NavigationMenuItem } from '@repo/ui/NavigationMenuItem'
import { PopupWithBackdrop } from '@repo/ui/PopupWithBackdrop'
import { useDocumentBundleWizardStore } from '@repo/store/useDocumentBundleWizardStore'
import { useEmailCardStore } from '@repo/store/useEmailCardStore'
import { useGlobalSettingsStore } from '@repo/store/useGlobalSettingsStore'
import { useKeyDateStore } from '@repo/store/useKeyDateStore'
import { useMattersStore } from '@repo/store/useMattersStore'
import { useLookupsStore } from '@repo/store/useLookupsStore'
import { useMenuItemsStore } from '@repo/store/useMenuItemsStore'
import { useMenuState, MenuItem, SubMenu, MenuDivider } from '@szhsin/react-menu'
import { useNewClientStore } from '@repo/store/useNewClientStore'
import { useNewMatterStore } from '@repo/store/useNewMatterStore'
import { useRef } from 'react'
import { useRouteLoaderData } from 'react-router-dom'
import { useUrlStore } from '@repo/store/useUrlStore'
import MasterActionButtonItems from './MasterActionButtonItems'
import MUIButton from '@mui/material/Button'

export const MasterActionButton = () => {
  const [menuState, toggleMenu] = useMenuState({ transition: true })
  const { allowedMenuIds } = useMenuItemsStore()
  const { currentClientMatterNo } = useMattersStore()
  const { toggleKeyDatesDrawer } = useKeyDateStore()
  const { resetWizardClientState } = useNewClientStore()
  const { resetWizardMatterState } = useNewMatterStore()
  const { resetBranch, resetDepartment, resetWorkflowType, enableBundledocs } = useLookupsStore()
  const { reset: resetDocumentBundleWizardStore } = useDocumentBundleWizardStore()
  const { setEmailModalOpen } = useEmailCardStore()
  const { useMicrosoftEmail, companyCanUseMicrosoftEmail } = useGlobalSettingsStore()
  const { setPostLoadAction, iframeSrc, routeId, navigateWithClientMatterNo } = useUrlStore()
  const loaderData = useRouteLoaderData(routeId)
  const ref = useRef(null)

  const handleMenuItemClick = (target, postLoadAction, appendClientMatterNoToTarget, clientMatterNo, label) => {
    const iframe = document.querySelector('iframe[name="ospreyIframe"]')
    const { contentWindow } = iframe || {} // the content of the iframe

    if (label === 'Email' && useMicrosoftEmail === true && companyCanUseMicrosoftEmail === true) {
      // show React email window for use with Microsoft email
      setEmailModalOpen({ isOpen: true })
      toggleMenu(false)
      return
    }

    if (target === 'NEWKEYDATE') {
      toggleKeyDatesDrawer({ keyDatesDrawerIsOpen: true, keyDatesDrawerMode: 'create' })
      toggleMenu(false)
      return
    }

    let shouldNavigate = target !== routeId

    if (postLoadAction) {
      const postLoadSelector = postLoadAction === 'sendEmail' ? '#pra-send-email-client' : '#pra-send-sms'
      let postLoadPage = 'matterpage.aspx'
      // if the iframe is not on the postLoadPage, wait for the iframe to load before executing the postLoadAction
      let postLoadActionShouldWaitForIframeToLoad = shouldNavigate || !iframeSrc.toLowerCase().includes(postLoadPage)

      // check to see if the selector is on the page already, in which case we don't need to navigate
      const element = contentWindow?.document?.querySelector(postLoadSelector)
      if (element && loaderData && loaderData.iframeVisible !== false) {
        shouldNavigate = false
        postLoadActionShouldWaitForIframeToLoad = false
        postLoadPage = iframeSrc.split('?')[0]
      }

      setPostLoadAction({ postLoadAction, postLoadActionShouldWaitForIframeToLoad, postLoadSelector, postLoadPage })
    }

    if (shouldNavigate) {
      navigateWithClientMatterNo({
        route: target,
        clientMatterNo,
        appendClientMatterNo: appendClientMatterNoToTarget
      })
    }

    toggleMenu(false)
  }

  return (
    <>
      <MUIButton
        startIcon={
          <AddIconNoCircle
            sx={{ color: 'white', fontSize: '24px', height: '24px', width: '24px', ml: '4px', mr: '-2px' }}
          />
        }
        variant="contained"
        sx={{
          backgroundColor: 'success.light',
          borderRadius: 20,
          padding: '6px 12px 6px 8px',
          height: 36,
          width: 'auto',
          '&:hover': {
            backgroundColor: 'success.lightHover'
          }
        }}
        onClick={() => toggleMenu(true)}
        ref={ref}
        data-testid="MasterActionButton"
      >
        New
      </MUIButton>

      <PopupWithBackdrop
        menuState={menuState}
        anchorRef={ref}
        toggleMenu={toggleMenu}
        className="masterActionMenu"
        arrow
      >
        {MasterActionButtonItems.map(
          ({
            label,
            icon,
            target,
            children,
            requiresCurrentMatter,
            postLoadAction,
            divider,
            menuItemId,
            appendClientMatterNoToTarget
          }) => {
            if (divider) return <MenuDivider key={label} />

            if (children) {
              return (
                <SubMenu
                  key={label}
                  label={NavigationMenuItem({ label, icon, isSubMenu: true, sx: { cursor: 'default' } })}
                >
                  {children.map(
                    ({
                      label,
                      target,
                      postLoadAction,
                      requiresCurrentMatter,
                      requiresBundledocsEnabled,
                      menuItemId,
                      appendClientMatterNoToTarget
                    }) => {
                      if (!allowedMenuIds.includes(menuItemId)) return null
                      if (requiresBundledocsEnabled && !enableBundledocs) return null

                      return (
                        <MenuItem
                          key={label}
                          onClick={() => {
                            handleMenuItemClick(
                              target,
                              postLoadAction,
                              appendClientMatterNoToTarget,
                              currentClientMatterNo,
                              label
                            )
                            resetDocumentBundleWizardStore()
                          }}
                          disabled={requiresCurrentMatter && !currentClientMatterNo}
                        >
                          {label}
                        </MenuItem>
                      )
                    }
                  )}
                </SubMenu>
              )
            }

            if (!allowedMenuIds.includes(menuItemId)) return null

            return (
              <MenuItem
                key={label}
                onClick={() => {
                  handleMenuItemClick(
                    target,
                    postLoadAction,
                    appendClientMatterNoToTarget,
                    currentClientMatterNo,
                    label
                  )
                  resetWizardClientState()
                  resetWizardMatterState()
                  resetBranch()
                  resetDepartment()
                  resetWorkflowType()
                }}
                disabled={requiresCurrentMatter && !currentClientMatterNo}
              >
                {icon || null}
                {label}
              </MenuItem>
            )
          }
        )}
      </PopupWithBackdrop>
    </>
  )
}
